// Route helpers shared by the Horizon admin shell (sidebar, navbar, routing).
import React from "react";
import { Route } from "react-router-dom";

/**
 * Whether a child route is visible for a given user right level.
 * @param {Object} child - child route, may carry a minRight threshold
 * @param {number} userRight - connected user's right level
 * @returns {boolean} true when the item should be shown
 */
export function isChildVisible(child, userRight) {
  return child.minRight === undefined || userRight >= child.minRight;
}

/**
 * Finds the display name of the route matching the current pathname.
 * @param {Array} routes - route configuration list (groups + leaves)
 * @param {string} pathname - current location pathname
 * @returns {string} the active route name, or a default label
 */
export function getActiveRouteName(routes, pathname) {
  for (const route of routes) {
    if (route.type === "group") {
      const child = route.children.find((c) => pathname === c.layout + c.path);
      if (child) return child.name;
    } else if (pathname === route.layout + route.path) {
      return route.name;
    }
  }
  return "Tableau de Bord";
}

/**
 * Builds a <Route> for one route entry. All views are Chakra-based.
 * @param {Object} route - route entry ({path, component})
 * @param {string|number} key - React key for the Route element
 * @returns {React.ReactElement}
 */
function routeElement(route, key) {
  const Component = route.component;
  return <Route key={key} path={route.path} element={<Component />} />;
}

/**
 * Flattens grouped/ungrouped routes into <Route> elements for the router.
 * @param {Array} routes - route configuration list
 * @returns {Array<React.ReactElement>} flat list of Route elements
 */
export function renderRoutes(routes) {
  return routes.flatMap((prop, key) => {
    if (prop.type === "group") {
      return prop.children.map((child, ck) => routeElement(child, `${key}-${ck}`));
    }
    return routeElement(prop, key);
  });
}
